/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SettingsSection } from '../constants/settings-nav';
import { useAgencyPermissions } from '@/features/agency';
import { getSettingsNav } from '../constants/settings-nav';
import { UnstyledButton } from '@/shared/ui';

interface SettingsSectionPickerProps {
  active: SettingsSection;
  onNavigate: (section: SettingsSection) => void;
  ariaLabel: string;
  isSoloBroker?: boolean;
}

const SCROLL_STEP = 120;

/**
 * Builds the inline style for a section chip in the mobile picker.
 * @param isActive - Whether the chip represents the active section.
 * @returns The CSS style object for the chip.
 */
function chipStyle(isActive: boolean): React.CSSProperties {
  return {
    flex: '0 0 auto',
    padding: 'var(--space-2) var(--space-4)',
    fontSize: 'var(--text-body-sm)',
    fontWeight: isActive ? 600 : 500,
    color: isActive ? 'var(--button-primary-text)' : 'var(--text-secondary)',
    background: isActive ? 'var(--seed-base)' : 'var(--surface-secondary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '9999px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'var(--font-family)',
  };
}

/**
 * Builds the inline style for a left/right scroll arrow overlay.
 * @param side - Which edge the arrow is anchored to.
 * @returns The CSS style object for the arrow button.
 */
function arrowStyle(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    [side]: 0,
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--surface-primary)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '50%',
    cursor: 'pointer',
    boxShadow: 'var(--shadow-sm)',
    zIndex: 2,
    color: 'var(--text-primary)',
    fontSize: 14,
  };
}

const FADE_MASK = 'linear-gradient(to right, black 85%, transparent 100%)';

/**
 * Horizontally scrollable section picker for the mobile settings layout.
 * @param props - Component props.
 * @param props.active - Currently active settings section.
 * @param props.onNavigate - Callback invoked when a section chip is tapped.
 * @param props.ariaLabel - Accessible label for the navigation container.
 * @param props.isSoloBroker - Hides the team/permission chips when true.
 * @returns The scrollable section picker element.
 */
export function SettingsSectionPicker({ active, onNavigate, ariaLabel, isSoloBroker }: SettingsSectionPickerProps) {
  const { t } = useTranslation('features/agency-settings');
  const {
    canManageSalePaperworkMilestones, canManageLeaseSettings, canManagePropertySettings,
  } = useAgencyPermissions();
  const navItems = getSettingsNav(t, {
    isSoloBroker,
    canManageSalePaperwork: canManageSalePaperworkMilestones,
    canManageLeaseSettings,
    canManagePropertySettings,
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollState]);

  const scrollBy = useCallback((direction: 'left' | 'right') => {
    scrollRef.current?.scrollBy({
      left: direction === 'right' ? SCROLL_STEP : -SCROLL_STEP,
      behavior: 'smooth',
    });
  }, []);

  return (
    <nav
      data-testid="settings-section-picker"
      aria-label={ariaLabel}
      style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
    >
      {canScrollLeft && (
        <UnstyledButton
          data-testid="picker-arrow-left"
          aria-label={t('v2.nav.scrollLeft')}
          onClick={() => scrollBy('left')}
          style={arrowStyle('left')}
          type="button"
        >
          ‹
        </UnstyledButton>
      )}
      <div
        data-testid="picker-scroll-container"
        ref={scrollRef}
        onScroll={updateScrollState}
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: 'var(--space-1)',
          paddingLeft: canScrollLeft ? 'var(--space-8)' : 'var(--space-1)',
          paddingRight: canScrollRight ? 'var(--space-8)' : 'var(--space-1)',
          scrollbarWidth: 'none',
          flex: 1,
          minWidth: 0,
          maskImage: canScrollRight ? FADE_MASK : undefined,
          WebkitMaskImage: canScrollRight ? FADE_MASK : undefined,
        }}
      >
        {navItems.map((item) => (
          <UnstyledButton
            key={item.key}
            onClick={() => onNavigate(item.key)}
            style={chipStyle(item.key === active)}
            aria-current={item.key === active ? 'page' : undefined}
            type="button"
          >
            {item.label}
          </UnstyledButton>
        ))}
      </div>
      {canScrollRight && (
        <UnstyledButton
          data-testid="picker-arrow-right"
          aria-label={t('v2.nav.scrollRight')}
          onClick={() => scrollBy('right')}
          style={arrowStyle('right')}
          type="button"
        >
          ›
        </UnstyledButton>
      )}
    </nav>
  );
}
